/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Dialog, IconButton, InlineNotification, Pagination, Search, Typography2 } from '@coursera/cds-core';
import { AddIcon, TrashIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import LearnerTableSkeletonRows from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/LearnerTableSkeletonRows';
import { learnerTableStyles } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/learnerTableStyles';
import type { AudienceDelta, Learner } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import type { LearnerListAudience } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';
import { LEARNER_LIST_PAGE_SIZE } from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';
import { useLearnerListEntryPoint } from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerListEntryPoint';
import { useMessagingTrackingData } from 'bundles/enterprise-admin-messaging-v2/hooks/useMessagingTrackingData';
import {
  MESSAGING_BANNER,
  MESSAGING_BUTTON,
  MESSAGING_MODAL,
  MESSAGING_TEXT_INPUT,
} from 'bundles/enterprise-admin-messaging-v2/utils/eventing';

import _t from 'i18n!nls/enterprise-admin-messaging-v2';

type Props = {
  /** The cohort the editor opens with — the user's working copy starts here. */
  initialLearners: Learner[];
  /**
   * Filter-backed flow: the pool is fetched server-side from this ready
   * `{ orgId, filter }` audience and edits are tracked as a delta. Absent for
   * the manual flow.
   */
  audience?: LearnerListAudience;
  /** Delta to seed the editor with when re-opening (filter flow). */
  initialDelta?: AudienceDelta;
  /** Persist the working copy back to the orchestrator and navigate to Review. */
  onSave: (next: Learner[]) => void;
  /** Persist the edit delta (filter flow) and navigate to Review. */
  onSaveDelta?: (delta: AudienceDelta) => void;
  /**
   * Restore the AI cohort and navigate to the read-only Learner list. Also
   * clears the manual-edit flag in the orchestrator.
   */
  onResetToOriginal: () => void;
};

const styles = {
  body: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);

    /* CDS Search's outer wrapper + inner input root both need an explicit
       width: 100% to fill the parent — the inputRoot carries
       min-width: 240px but no width override of its own. */
    .cds-Search-base,
    .cds-Search-inputRoot {
      width: 100%;
    }
  `,
  actionCell: css`
    /* Row 10: wide enough that the "+ Add" button isn't clipped. The table is
       table-layout: fixed inside an overflow:hidden card, so the column can't
       grow to its content — it has to be sized here. */
    width: 120px;
    text-align: right;
    white-space: nowrap;
  `,
  loadError: css`
    /* Always-mounted live region: keep it in the DOM for screen readers and
       collapse it (no flex gap) until an error is actually showing. */
    :empty {
      display: none;
    }
  `,
};

/**
 * Editable cohort: trash an existing learner to remove them; type into the
 * search box to surface learners outside the cohort with an "+ Add" pill.
 * Working copy lives locally; Save changes commits back to the orchestrator.
 */
const EditLearnerListScreen = ({
  initialLearners,
  audience,
  initialDelta,
  onSave,
  onSaveDelta,
  onResetToOriginal,
}: Props) => {
  const [bannerDismissed, setBannerDismissed] = React.useState(false);

  // All entry-point-specific behaviour (server fetch vs in-memory list, search,
  // pagination, add/remove, and the save payload) lives in this hook; the screen
  // just renders the rows and forwards the affordances.
  const {
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    pageCount,
    pageRows,
    filteredCount,
    loading,
    error,
    addLearner,
    removeLearner,
    save,
  } = useLearnerListEntryPoint({ initialLearners, audience, initialDelta });

  const track = useTracker();
  const trackingData = useMessagingTrackingData();

  // Impression: fires once when the cohort editor appears.
  React.useEffect(() => {
    track('view_modal', { ...trackingData, modal: { name: MESSAGING_MODAL.EDIT_LEARNERS } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Banner impression: fires while the dismissible edit-warning banner is visible.
  React.useEffect(() => {
    if (!bannerDismissed) {
      track('view_banner', {
        bannerName: MESSAGING_BANNER.EDIT_LEARNERS_WARNING,
        ...trackingData,
        modal: { name: MESSAGING_MODAL.EDIT_LEARNERS },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerDismissed]);

  /** Fire the text-input focus event for the search/add learners field. */
  const handleSearchFocus = React.useCallback<() => void>(() => {
    track('click_text_input', {
      textInput: { name: MESSAGING_TEXT_INPUT.LEARNER_SEARCH },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.EDIT_LEARNERS },
    });
  }, [track, trackingData]);

  const handleSave = React.useCallback<() => void>(() => {
    track('click_button', {
      button: { name: MESSAGING_BUTTON.SAVE_LEARNERS },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.EDIT_LEARNERS },
    });
    if (save.kind === 'filter') {
      onSaveDelta?.(save.delta);
    } else {
      onSave(save.learners);
    }
  }, [track, trackingData, save, onSaveDelta, onSave]);

  const handleReset = React.useCallback<() => void>(() => {
    // No local state reset needed — onResetToOriginal restores the original
    // cohort in the orchestrator and navigates away, unmounting this screen.
    track('click_button', {
      button: { name: MESSAGING_BUTTON.RESET_LEARNER_LIST },
      ...trackingData,
      modal: { name: MESSAGING_MODAL.EDIT_LEARNERS },
    });
    onResetToOriginal();
  }, [track, trackingData, onResetToOriginal]);

  return (
    <React.Fragment>
      <Dialog.Content>
        <div css={styles.body}>
          {!bannerDismissed && (
            <InlineNotification
              severity="information"
              onDismiss={() => setBannerDismissed(true)}
              data-testid="ai-message-modal-edit-learner-banner"
            >
              {_t('Editing learner list will remove auto updates in learner cohort based on AI')}
            </InlineNotification>
          )}

          <Search
            aria-label={_t('Search and add learners')}
            value={searchQuery}
            onChange={setSearchQuery}
            onFocus={handleSearchFocus}
            placeholder={_t('Search and add learners')}
            hideSearchButton
            data-testid="ai-message-modal-edit-learner-search"
          />

          {/* Always-mounted live region so the error is announced when it appears. */}
          <div role="alert" css={styles.loadError}>
            {error ? (
              <Typography2 component="span" variant="bodySecondary" color="error">
                {_t('Something went wrong loading learners. Please try again.')}
              </Typography2>
            ) : null}
          </div>

          <div css={learnerTableStyles.tableWrapper}>
            <table
              css={learnerTableStyles.table}
              aria-label={_t('Add or remove learners')}
              aria-busy={loading}
              data-testid="ai-message-modal-edit-learner-table"
            >
              <thead>
                <tr>
                  <th css={[learnerTableStyles.th, learnerTableStyles.nameColumn]}>
                    <Typography2 component="span" variant="subtitleMedium" color="supportText">
                      {_t('Name')}
                    </Typography2>
                  </th>
                  <th css={learnerTableStyles.th}>
                    <Typography2 component="span" variant="subtitleMedium" color="supportText">
                      {_t('Email')}
                    </Typography2>
                  </th>
                  <th css={[learnerTableStyles.th, styles.actionCell]} aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {loading && pageRows.length === 0 ? (
                  <LearnerTableSkeletonRows rowCount={LEARNER_LIST_PAGE_SIZE} columnCount={3} />
                ) : !error && pageRows.length === 0 ? (
                  <tr>
                    <td css={learnerTableStyles.emptyRow} colSpan={3}>
                      <Typography2 component="span" variant="bodySecondary">
                        {_t('No learners match your search.')}
                      </Typography2>
                    </td>
                  </tr>
                ) : (
                  pageRows.map(({ learner, inList }) => (
                    <tr key={learner.id} data-testid={`ai-message-modal-edit-learner-row-${learner.id}`}>
                      <td css={learnerTableStyles.td}>
                        {/* Row 10: learners not yet added read grey to set them apart from the cohort. */}
                        <Typography2 component="span" variant="bodyPrimary" color={inList ? undefined : 'supportText'}>
                          {learner.name}
                        </Typography2>
                      </td>
                      <td css={learnerTableStyles.td}>
                        <Typography2 component="span" variant="bodyPrimary" color={inList ? undefined : 'supportText'}>
                          {learner.email}
                        </Typography2>
                      </td>
                      <td css={[learnerTableStyles.td, styles.actionCell]}>
                        {inList ? (
                          <IconButton
                            variant="ghost"
                            size="small"
                            icon={<TrashIcon />}
                            aria-label={_t('Remove #{name}', { name: learner.name })}
                            tooltip={_t('Remove from list')}
                            onClick={() => removeLearner(learner.id)}
                            data-testid={`ai-message-modal-edit-learner-remove-${learner.id}`}
                          />
                        ) : (
                          <Button
                            variant="secondary"
                            size="small"
                            icon={<AddIcon />}
                            iconPosition="before"
                            onClick={() => addLearner(learner)}
                            data-testid={`ai-message-modal-edit-learner-add-${learner.id}`}
                          >
                            {_t('Add')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {pageCount > 1 && (
              <div css={learnerTableStyles.paginationRow}>
                <Pagination
                  total={filteredCount}
                  pageSize={LEARNER_LIST_PAGE_SIZE}
                  page={page}
                  onPageChange={(_event, nextPage) => setPage(nextPage)}
                  hidePageSizeInput
                  aria-label={_t('Add or remove learners pagination')}
                />
              </div>
            )}
          </div>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <div css={learnerTableStyles.actions}>
          <Button variant="primary" onClick={handleSave} data-testid="ai-message-modal-edit-learner-save">
            {_t('Save changes')}
          </Button>
          <Button variant="secondary" onClick={handleReset} data-testid="ai-message-modal-edit-learner-reset">
            {_t('Go back to original list')}
          </Button>
        </div>
      </Dialog.Actions>
    </React.Fragment>
  );
};

export default EditLearnerListScreen;
